/**
 * Каталог событий Ядра (Core).
 *
 * Ядро эмитит эти события, плагины на них подписываются.
 * Контракт однонаправленный: плагины зависят от Ядра, Ядро не знает о плагинах.
 */
import type { Writeoff } from '@core/db/models'

/** События Ядра. */
export enum PosEvent {
  /** Перед фиксацией продажи. Хендлеры могут наложить veto (например, Честный Знак). */
  PRE_SALE = 'PRE_SALE',
  /** Продажа успешно завершена (пост-обработка: Меркурий гасит ВСД и т.п.). */
  SALE_COMPLETED = 'SALE_COMPLETED',
  /** Документ списания успешно создан (пост-обработка: вывод кодов ЧЗ). */
  WRITEOFF_COMPLETED = 'WRITEOFF_COMPLETED',
  /** Запрошена оплата (например, динамическая привязка суммы к СБП QR). */
  PAYMENT_REQUESTED = 'PAYMENT_REQUESTED',
  /** Сканер штрихкода вернул строку (HID-режим). */
  BARCODE_SCANNED = 'BARCODE_SCANNED',
  /** Каталог обновлён (импортом или через облачный дельта-синк). */
  CATALOG_UPDATED = 'CATALOG_UPDATED',
  /** Пул вскрытых тар обновлён из облака (PULL) — касса перечитывает список. */
  CONTAINERS_UPDATED = 'CONTAINERS_UPDATED',
  /** Смена зоны изменилась из облака (присоединение/закрытие) — UI обновляется. */
  SHIFTS_UPDATED = 'SHIFTS_UPDATED',
  /** Статус заявки с кассы изменился (отправлена/одобрена/отклонена). */
  PENDING_UPDATED = 'PENDING_UPDATED',
  /** Справочник кассиров из облака обновился (права могли измениться). */
  CASHIERS_UPDATED = 'CASHIERS_UPDATED',
  /** Модуль маркировки получил статус кода (сканирование → фоновая проверка). */
  MARKING_STATUS_UPDATED = 'MARKING_STATUS_UPDATED',
  /** Директор включил/выключил модуль в админке. */
  MODULE_ENABLED_CHANGED = 'MODULE_ENABLED_CHANGED'
}

/** Позиция чека. */
export interface SaleItem {
  /** Стабильный id строки корзины (line-level возврат, 65.10). */
  lineId?: string
  /** GTIN товара (при наличии). */
  gtin?: string
  name: string
  qty: number
  price: number
  /** Код маркировки DataMatrix (Честный Знак), если позиция маркирована. */
  datamatrix?: string
  /**
   * Позиция подлежит обязательной маркировке (эвристика молочки / учёт в ЧЗ).
   * Плагин Честного Знака использует это для решения о veto на PRE_SALE.
   */
  requiresMarking?: boolean
  /** Кассир осознанно продал позицию без кода (право `can_skip_marking`). */
  markingSkipped?: boolean
  /** Код позиции не прошёл проверку ЧЗ. */
  markingInvalid?: boolean
  /** Причина пропуска/невалидного кода. */
  markingReason?: string
  /** Статус проверки кода позиции в ЧЗ (65.12). */
  czStatus?: 'valid' | 'invalid' | 'unknown'
}

/** Контекст продажи — передаётся в PRE_SALE и SALE_COMPLETED. */
export interface SaleContext {
  receiptId: string
  items: SaleItem[]
  total: number
  /**
   * Признак расчёта (ARCH-TASK-65). `income_return` — возврат: плагины
   * пост-обработки (Меркурий/ЧЗ) не должны гасить ВСД и коды повторно.
   */
  settlementType?: 'income' | 'income_return' | 'outcome' | 'outcome_return'
}

/**
 * Карта соответствия события и типа его payload.
 * Обеспечивает строгую типизацию on/off/emit.
 */
export interface EventPayloadMap {
  [PosEvent.PRE_SALE]: SaleContext
  [PosEvent.SALE_COMPLETED]: SaleContext
  [PosEvent.WRITEOFF_COMPLETED]: Writeoff
  [PosEvent.PAYMENT_REQUESTED]: { receiptId: string; amount: number; method: string }
  [PosEvent.BARCODE_SCANNED]: { raw: string }
  [PosEvent.CATALOG_UPDATED]: { versionId: number; count: number }
  [PosEvent.CONTAINERS_UPDATED]: Record<string, never>
  [PosEvent.SHIFTS_UPDATED]: Record<string, never>
  [PosEvent.PENDING_UPDATED]: Record<string, never>
  [PosEvent.CASHIERS_UPDATED]: Record<string, never>
  [PosEvent.MARKING_STATUS_UPDATED]: {
    code: string
    status: 'valid' | 'invalid' | 'unknown'
    reason?: string
  }
  [PosEvent.MODULE_ENABLED_CHANGED]: {
    id: string
    enabled: boolean
    /** Изменение сохранено локально, но ещё не подтверждено облаком. */
    pending?: boolean
  }
}
